/**
 * Where a profiled run spent its time.
 *
 * One component serves both surfaces that show it — the sandbox result panel
 * and the task detail screen — so the two can never drift apart.
 *
 * The phase bar is the headline and the function table is the detail. Phase
 * colours are fixed semantics rather than a palette: amber marks the import
 * cost, which is the recoverable part, and the accent marks execute, which is
 * the work itself. The cold-start line states the number that decides whether
 * keeping workers warm would be worth it.
 */
import { useQuery } from '@tanstack/react-query';

import { getTaskProfile } from '@optum/api-client/endpoints/tasks';
import type { TaskProfile, TaskProfilePhases } from '@optum/api-client/types';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { qk } from '../../lib/query-keys.js';

import { StatRow } from '../../ui/StatRow.js';
import { Icon, Inline, Typography } from '../../ui/finixt.js';
import { ProfileFunctionTable } from './ProfileFunctionTable.js';

const PHASES: { key: keyof TaskProfilePhases; label: string; color: string }[] = [
  { key: 'spawn_ms', label: 'Worker spawn', color: 'var(--grey-500)' },
  { key: 'import_ms', label: 'Plugin import', color: 'var(--warn)' },
  { key: 'init_ms', label: 'Init', color: 'var(--violet)' },
  { key: 'execute_ms', label: 'Execute', color: 'var(--fill-action)' },
];

/** Milliseconds, switching to seconds past a thousand — tiles only. */
export function durationTile(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${Math.round(ms)} ms`;
}

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(0)} MiB`;
}

function count(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)} k`;
  return String(n);
}

export function ProfilePanel({ taskId }: { taskId: string }) {
  const profile = useQuery({
    queryKey: qk.taskProfile(taskId),
    queryFn: () => getTaskProfile(taskId),
  });

  if (profile.isPending) return <LoadingBlock />;
  if (profile.isError) {
    return (
      <ErrorBlock
        message={
          profile.error instanceof Error ? profile.error.message : 'Failed to load the profile'
        }
      />
    );
  }
  return <ProfileView profile={profile.data} />;
}

export function ProfileView({ profile }: { profile: TaskProfile }) {
  const { phases, totals, functions } = profile;
  const measured = PHASES.reduce((sum, p) => sum + (phases[p.key] || 0), 0);
  // The bar divides what was measured; wall time is the headline beside it.
  const span = measured > 0 ? measured : 1;
  const coldMs = (phases.spawn_ms || 0) + (phases.import_ms || 0);
  const coldShare = totals.wall_ms > 0 ? Math.round((coldMs / totals.wall_ms) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 11,
          }}
        >
          <Typography variant="card">Where the time went</Typography>
          <span
            style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
            className="mono"
          >
            {Math.round(totals.wall_ms)} ms wall
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            height: 8,
            width: '100%',
            overflow: 'hidden',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--surface-fill)',
          }}
        >
          {PHASES.map((p) => {
            const ms = phases[p.key] || 0;
            if (ms <= 0) return null;
            return (
              <div
                key={p.key}
                title={`${p.label}: ${Math.round(ms)} ms`}
                style={{ width: `${(ms / span) * 100}%`, background: p.color }}
              />
            );
          })}
        </div>

        <Inline gap={16}>
          {PHASES.map((p) => (
            <span
              key={p.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 'var(--text-meta-lg)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 'var(--radius-pill)',
                  background: p.color,
                }}
              />
              <span style={{ color: 'var(--text-muted)' }}>{p.label}</span>
              <span style={{ color: 'var(--text-strong)' }} className="mono">
                {Math.round(phases[p.key] || 0)} ms
              </span>
            </span>
          ))}
        </Inline>

        <p
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 7,
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-fill)',
            padding: 11,
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--text-muted)',
          }}
        >
          <Icon name="info" size={14} style={{ marginTop: 5, flexShrink: 0 }} />
          <span>
            Cold start (spawn + plugin import) accounts for{' '}
            <span style={{ color: 'var(--text-strong)' }} className="mono">
              {Math.round(coldMs)} ms
            </span>{' '}
            — {coldShare}% of this run.
          </span>
        </p>
      </div>

      <StatRow
        tiles={[
          { label: 'Wall', value: durationTile(totals.wall_ms), icon: 'timer' },
          { label: 'CPU', value: durationTile(totals.cpu_ms), icon: 'memory' },
          { label: 'Calls', value: count(totals.calls), icon: 'functions' },
          { label: 'Peak RSS', value: bytes(totals.peak_rss_bytes), icon: 'database' },
        ]}
      />

      <ProfileFunctionTable functions={functions} wallMs={totals.wall_ms} />
    </div>
  );
}
